import { MouseEvent, ReactNode } from 'react';
import { ButtonBase, Typography } from '@mui/material';
import clsx from 'clsx';
import { useResultItemRowStyles } from './result-item-row.styles';

export interface ResultItemRowProps {
  icon: ReactNode;
  primary: string;
  /** Secondary detail lines; falsy entries are skipped so callers can pass optional fields directly. */
  secondaryLines?: (string | undefined | false)[];
  /** Optional muted pill shown beside the primary label (e.g. an "Inactive" status). */
  tag?: string;
  /** Optional right-hand column (e.g. status badge + project logo), top-aligned with the primary label. */
  trailing?: ReactNode;
  /** Detail route. Rendered as an anchor so the row supports open-in-new-tab (⌘/Ctrl/middle click). */
  href?: string;
  /** In-app (SPA) navigation for a plain left click; modifier/middle clicks fall through to the browser. */
  onClick?: () => void;
}

/**
 * Shared presentational row for a single search result — icon + primary label + optional secondary
 * lines. Renders as an anchor when `href` is provided (so the browser handles open-in-new-tab),
 * otherwise as a static row.
 */
export function ResultItemRow({ icon, primary, secondaryLines, tag, trailing, href, onClick }: ResultItemRowProps) {
  const classes = useResultItemRowStyles();
  // Filter out lines that are empty
  const lines = (secondaryLines ?? []).filter((line): line is string => !!line);

  const content = (
    <>
      <span className={classes.icon}>{icon}</span>
      <span className={classes.text}>
        <span className={classes.primaryRow}>
          {/* An empty label would still take the row's gap and push the tag off the left edge. */}
          {primary.trim() && (
            <Typography component="span" variant="body1" className={classes.primary}>
              {primary}
            </Typography>
          )}
          {tag && <span className={classes.tag}>{tag}</span>}
        </span>
        {lines.map((line, index) => (
          <Typography key={index} component="span" variant="body2" color="textSecondary" className={classes.secondary}>
            {line}
          </Typography>
        ))}
      </span>
      {trailing && <span className={classes.trailing}>{trailing}</span>}
    </>
  );

  if (href) {
    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      // Let the browser open a new tab/window for modifier or non-primary clicks via the href.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      onClick?.();
    };

    return (
      <ButtonBase
        component="a"
        href={href}
        className={clsx(classes.root, classes.clickable)}
        onClick={handleClick}
        focusRipple
      >
        {content}
      </ButtonBase>
    );
  }

  return <div className={classes.root}>{content}</div>;
}
